import { Injectable } from '@angular/core';
import { DbTransaction } from '../models/db/db-transaction';
import { StoreName } from '../entities/StoreName';
import { RepositoryService } from '../baseServices/repository.service';
import { FormDocumentMedia } from '../entities/FormDocumentMedia';
import { RecordNotFoundException } from '../utils/exceptions';
import { GlobalConstants } from '../constant/constant';
import { FormDocumentMediaThumbnail } from '../models/form-document-media/form-document-media-thumbnail';

@Injectable({
  providedIn: 'root',
})
export class FormDocumentMediaService {
  constructor(private repository: RepositoryService) {}

  // #region Utility

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.FormDocumentMedia], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getFormDocumentMediaByRecordId(recordId: number, existingTransaction?: DbTransaction): Promise<FormDocumentMedia> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<FormDocumentMedia>(StoreName.FormDocumentMedia, recordId, FormDocumentMedia, transaction);
    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as FormDocumentMedia;

    return record;
  }

  async getFormDocumentMediaThumbnailByRecordId(recordId: number, existingTransaction?: DbTransaction): Promise<FormDocumentMediaThumbnail> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    
    const record = await this.repository.getOnePartialFieldsByIndex(StoreName.FormDocumentMedia, GlobalConstants.KEY_NAMES.RecordId, recordId, FormDocumentMediaThumbnail, undefined, transaction);
    if (record === null) {
      throw new RecordNotFoundException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getFormDocumentMediaByRecordIds(recordIds: number[], existingTransaction?: DbTransaction): Promise<FormDocumentMedia[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const records = await this.repository.getAllByIndex<FormDocumentMedia>(StoreName.FormDocumentMedia, GlobalConstants.KEY_NAMES.RecordId, recordIds, FormDocumentMedia, transaction);

    return records;
  }

  async getFormDocumentMediaByDocumentIds(documentIds: string[], existingTransaction?: DbTransaction): Promise<FormDocumentMedia[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const records = await this.repository.getAllByIndex<FormDocumentMedia>(StoreName.FormDocumentMedia, GlobalConstants.KEY_NAMES.DocumentId, documentIds, FormDocumentMedia, transaction);

    return records;
  }

  // #endregion
  // #region Low-level Create

  async createFormDocumentMedia(recordToCreate: FormDocumentMedia, existingTransaction?: DbTransaction) {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const recordId = await this.repository.create(recordToCreate, StoreName.FormDocumentMedia, transaction);
    return recordId;
  }

  // #endregion
  // #region Low-level Update

  async updateFormDocumentMedia(recordId: number, value: string, existingTransaction?: DbTransaction) {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const recordToUpdate = await this.getFormDocumentMediaByRecordId(recordId, transaction);
    recordToUpdate.Value = value;
    await this.repository.update(recordToUpdate, StoreName.FormDocumentMedia, transaction);
  }

  // #endregion
  // #region Low-level Delete

  async deleteFormDocumentMedia(recordId: number, existingTransaction?: DbTransaction) {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    await this.repository.delete(recordId, StoreName.FormDocumentMedia, transaction);
  }

  async deleteFormDocumentMediaByDocumentIds(documentIds: string[], existingTransaction?: DbTransaction) {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const records = await this.getFormDocumentMediaByDocumentIds(documentIds, transaction);
    await this.repository.deleteBulkData(records, StoreName.FormDocumentMedia);
  }

  // #endregion
}
